import React from "react";
import {
	Alert,
	Box,
	Chip,
	CircularProgress,
	Stack,
	Typography,
} from "@mui/material";
import BoltIcon from "@mui/icons-material/Bolt";
import WaterDropIcon from "@mui/icons-material/WaterDrop";
import { useTenantUtilitiesStatus } from "services/hooks/utilities";
import { currencyFormatter } from "utilities/currencyFormatter";

const formatBalanceValue = (value) => {
	if (value == null || value === "") return "—";
	if (typeof value === "number") {
		return Number.isFinite(value) ? currencyFormatter(value) : String(value);
	}
	if (typeof value === "string") {
		const asNum = Number(value);
		if (value.trim() !== "" && Number.isFinite(asNum)) return currencyFormatter(asNum);
		return value;
	}
	if (typeof value === "object") return null;
	return String(value);
};

const flattenBalanceRows = (balance, prefix = "") => {
	if (!balance || typeof balance !== "object" || Array.isArray(balance)) return [];
	const rows = [];
	Object.entries(balance).forEach(([key, value]) => {
		if (key === "raw" || key === "provider_payload") return;
		const label = prefix ? `${prefix}.${key}` : key;
		if (value != null && typeof value === "object" && !Array.isArray(value)) {
			rows.push(...flattenBalanceRows(value, label));
			return;
		}
		const formatted = formatBalanceValue(value);
		if (formatted != null) rows.push({ label, value: formatted });
	});
	return rows;
};

const balanceErrorMessage = (balanceError) => {
	if (!balanceError) return null;
	if (balanceError === "enrollment_inactive") return "Utilities enrollment is inactive for this unit.";
	if (typeof balanceError === "string") return balanceError;
	if (balanceError.detail) return balanceError.detail;
	return "Could not load live utility balance.";
};

const TenantUtilitiesStatus = ({ tenantId, occupancyId }) => {
	const { data, loading, error } = useTenantUtilitiesStatus(tenantId, occupancyId);

	if (!tenantId || !occupancyId) return null;

	if (loading) {
		return (
			<Box display="flex" alignItems="center" gap={1} justifyContent="flex-end">
				<CircularProgress size={18} />
				<Typography variant="body2" color="text.secondary">
					Loading utilities…
				</Typography>
			</Box>
		);
	}

	if (error) {
		return (
			<Alert severity="warning" sx={{ maxWidth: 420 }}>
				{error}
			</Alert>
		);
	}

	if (!data) return null;

	const enrolled = Boolean(data.enrolled);
	const utilities = data.utilities;
	const balanceRows = flattenBalanceRows(data.balance);
	const balanceErr = balanceErrorMessage(data.balance_error);

	if (!enrolled) {
		const inactive = utilities && utilities.is_active === false;
		return (
			<Box sx={{ maxWidth: 420, textAlign: "right" }}>
				<Chip
					size="small"
					label={inactive ? "Utilities inactive" : "Utilities not enabled"}
					color="default"
					variant="outlined"
					sx={{ mb: 0.5 }}
				/>
				<Typography variant="body2" color="text.secondary">
					{inactive
						? "This unit has a utilities enrollment that is currently inactive."
						: "Utilities not enabled for this unit."}
				</Typography>
			</Box>
		);
	}

	return (
		<Box sx={{ maxWidth: 440, textAlign: "right" }}>
			<Stack direction="row" spacing={1} justifyContent="flex-end" alignItems="center" flexWrap="wrap" sx={{ mb: 0.5 }}>
				<Chip size="small" label="Utilities enrolled" color="success" variant="outlined" />
				{utilities?.provider_account_number && (
					<Typography variant="caption" color="text.secondary" sx={{ fontFamily: "monospace" }}>
						Acct {utilities.provider_account_number}
					</Typography>
				)}
			</Stack>
			<Stack direction="row" spacing={1} justifyContent="flex-end" flexWrap="wrap" sx={{ mb: 0.5 }}>
				{utilities?.electricity_enabled !== false && (
					<Chip size="small" icon={<BoltIcon />} label="Electricity" variant="outlined" />
				)}
				{utilities?.water_enabled !== false && (
					<Chip size="small" icon={<WaterDropIcon />} label="Water" variant="outlined" />
				)}
			</Stack>
			{balanceErr && (
				<Alert severity="warning" sx={{ mt: 0.5, textAlign: "left" }}>
					{balanceErr}
				</Alert>
			)}
			{!balanceErr && balanceRows.length > 0 && (
				<Box sx={{ mt: 0.5, textAlign: "left", bgcolor: "action.hover", borderRadius: 1, p: 1 }}>
					<Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
						Live balance
					</Typography>
					{balanceRows.slice(0, 8).map((row) => (
						<Stack key={row.label} direction="row" justifyContent="space-between" spacing={2}>
							<Typography variant="body2" color="text.secondary" sx={{ textTransform: "capitalize" }}>
								{row.label.replace(/_/g, " ")}
							</Typography>
							<Typography variant="body2" fontWeight={600}>
								{row.value}
							</Typography>
						</Stack>
					))}
				</Box>
			)}
			{!balanceErr && balanceRows.length === 0 && data.balance != null && (
				<Typography variant="body2" color="text.secondary">
					Balance loaded (no displayable fields).
				</Typography>
			)}
		</Box>
	);
};

export default TenantUtilitiesStatus;
